import { type FunctionComponent } from "react";

const toString = Function.prototype.toString,
	TYPE_ASYNC = { prefix: "async ƒ" },
	TYPE_ASYNC_GENERATOR = { prefix: "async ƒ*" },
	TYPE_CLASS = { prefix: "class" },
	TYPE_FUNCTION = { prefix: "ƒ" },
	TYPE_GENERATOR = { prefix: "ƒ*" };

// eslint-disable-next-line @typescript-eslint/ban-types
export const FunctionValue: FunctionComponent<{ f: Function }> = ({ f }) => {
	//
	// Largely inspired by `inspectFunction.js`.
	//

	let type: { prefix: string };
	let m: RegExpExecArray | null;
	const t = toString.call(f);

	switch (f.constructor && f.constructor.name) {
		case "AsyncFunction":
			type = TYPE_ASYNC;
			break;
		case "AsyncGeneratorFunction":
			type = TYPE_ASYNC_GENERATOR;
			break;
		case "GeneratorFunction":
			type = TYPE_GENERATOR;
			break;
		default:
			type = /^class\b/.test(t) ? TYPE_CLASS : TYPE_FUNCTION;
			break;
	}

	// A class, possibly named. For example, this:
	//
	//     class Name
	//
	// turns into this:
	//
	//     Name = class
	if (type === TYPE_CLASS) {
		return (
			<span className={functionClass}>
				<span className={prefixClass}>{type.prefix}</span>
			</span>
		);
	}

	// An arrow function with a single argument. For example, these:
	//
	//     arg => {}
	//     async arg => {}
	//
	// turn into these:
	//
	//     ƒ(arg)
	//     async ƒ(arg)
	if ((m = /^(?:async\s*)?(\w+)\s*=>/.exec(t))) {
		return (
			<span className={functionClass}>
				<span className={prefixClass}>{type.prefix}</span>({m[1]})
			</span>
		);
	}

	// An arrow function with parenthesized arguments. For example, these:
	//
	//     (arg1, arg2) => {}
	//     async (arg1, arg2) => {}
	//
	// turn into these:
	//
	//     ƒ(arg1, arg2)
	//     async ƒ(arg1, arg2)
	if ((m = /^(?:async\s*)?\(\s*(\w+(?:\s*,\s*\w+)*)?\s*\)/.exec(t))) {
		const args = m[1] ? m[1].replace(/\s*,\s*/g, ", ") : "";

		return (
			<span className={functionClass}>
				<span className={prefixClass}>{type.prefix}</span>({args})
			</span>
		);
	}

	// A function, possibly: async, generator, anonymous, simply arguments. For example, these:
	//
	//     function name(…)
	//     function* name(…)
	//     async function name(…)
	//     async function* name(…)
	//
	// turn into these:
	//
	//     name = ƒ(…)
	//     name = ƒ*(…)
	//     name = async ƒ(…)
	//     name = async ƒ*(…)
	if (
		(m = /^(?:async\s*)?function(?:\s*\*)?(?:\s*\w+)?\s*\(\s*(\w+(?:\s*,\s*\w+)*)?\s*\)/.exec(
			t
		))
	) {
		const args = m[1] ? m[1].replace(/\s*,\s*/g, ", ") : "";

		return (
			<span className={functionClass}>
				<span className={prefixClass}>{type.prefix}</span>({args})
			</span>
		);
	}

	// Something else, like destructuring, comments or default values.
	return (
		<span className={functionClass}>
			<span className={prefixClass}>{type.prefix}</span>(…)
		</span>
	);
};

const functionClass = "text-syntax-normal";
const prefixClass = "text-syntax-keyword";
